import type { Dispatch, SetStateAction } from 'react'
import * as Sentry from '@sentry/react'
import type { MindMapNode, MindMapEdge } from '@bandos/shared'
import { SuggestionsService } from '../../../services'
import type { AISuggestion, AIStateSetters, AIRefs } from '../types/aiTypes'
import { removeSuggestionsForNode } from './aiSuggestions'
import { calculateSingleNodePosition, type PositionCalculationDeps } from './aiPositioning'

// ============================================================================
// CLEAR ALL STATE
// ============================================================================

/**
 * Resets all AI state — used when all source nodes are dismissed.
 */
export function clearAllState(setters: AIStateSetters, refs: AIRefs): void {
  setters.setSuggestions([])
  setters.setCurrentOperation(null)
  setters.setIsProcessing(false)
  setters.setError(null)
  setters.setSourceNodes(new Map())
  refs.lastOperationRef.current = ''
  refs.lastNodeIdRef.current = ''
  refs.batchIdRef.current = 0
}

// ============================================================================
// DISMISS SOURCE NODE
// ============================================================================

/**
 * Dismisses a source node and all its pending suggestions (both locally and in DB).
 */
export async function dismissSourceNode(
  nodeId: string,
  mindmapId: string | undefined,
  setters: Pick<AIStateSetters, 'setSuggestions' | 'setSourceNodes'>,
  clearAllStateFn: () => void
): Promise<void> {
  // Dismiss in database
  if (mindmapId) {
    try {
      const result = await SuggestionsService.dismissAllForNode(mindmapId, nodeId)
      if (result.error) {
        console.error('Failed to dismiss suggestions in database:', result.error.message)
        Sentry.captureException(new Error(result.error.message), {
          tags: { feature: 'mindmap', action: 'dismiss_suggestions' },
        })
      }
    } catch (err) {
      console.error('Failed to dismiss suggestions in database:', err)
      Sentry.captureException(err, {
        tags: { feature: 'mindmap', action: 'dismiss_suggestions' },
      })
    }
  }

  // Remove from local state (functional updater avoids stale closure)
  setters.setSuggestions((prev) => removeSuggestionsForNode(prev, nodeId))

  setters.setSourceNodes((prev) => {
    const updated = new Map(prev)
    updated.delete(nodeId)
    if (updated.size === 0) {
      setTimeout(() => clearAllStateFn(), 0)
    }
    return updated
  })
}

// ============================================================================
// TOGGLE SUGGESTION (ACCEPT / REJECT)
// ============================================================================

interface ToggleCallbacks {
  onAddNodes: (nodes: MindMapNode[], edges: MindMapEdge[]) => void
  onRemoveNodes?: (nodeIds: string[]) => void
  onNodeAdded?: (node: MindMapNode) => void
  onGenerateFeedbackForIdea?: (nodeId: string) => void
}

interface ToggleOptions {
  autoChainEnabled?: boolean
}

/**
 * Accepts or rejects a suggestion.
 * On accept: adds node to mindmap, triggers feedback/proactive generation, removes from suggestions.
 * On reject: removes node from mindmap, marks suggestion as unselected.
 */
export function handleToggleSuggestion(
  suggestionId: string,
  suggestions: AISuggestion[],
  getPositionDeps: () => PositionCalculationDeps,
  setters: Pick<AIStateSetters, 'setSuggestions' | 'setSourceNodes'>,
  callbacks: ToggleCallbacks,
  options: ToggleOptions = {}
): Promise<void> {
  const targetSuggestion = suggestions.find((s) => s.id === suggestionId)
  if (!targetSuggestion) return Promise.resolve()

  const isSelecting = !targetSuggestion.selected

  if (isSelecting) {
    const parentNodeId = targetSuggestion.originalEdge?.source

    // Calculate position and add node
    const nodeCategory = targetSuggestion.originalNode.data.category
    const autoChainEnabled = options.autoChainEnabled ?? true
    const shouldAutoGenerate =
      autoChainEnabled && (nodeCategory === 'need' || nodeCategory === 'idea')

    const baseNode = parentNodeId
      ? {
          ...targetSuggestion.originalNode,
          position: calculateSingleNodePosition(parentNodeId, getPositionDeps()),
        }
      : targetSuggestion.originalNode

    // Set flag so the node's chat auto-triggers generation on mount
    const updatedNode = shouldAutoGenerate
      ? {
          ...baseNode,
          data: {
            ...baseNode.data,
            pendingChatGeneration: true,
          },
        }
      : baseNode

    callbacks.onAddNodes(
      [updatedNode],
      targetSuggestion.originalEdge ? [targetSuggestion.originalEdge] : []
    )
    callbacks.onNodeAdded?.(updatedNode)

    // Trigger feedback for idea nodes
    if (nodeCategory === 'idea') {
      setTimeout(() => {
        callbacks.onGenerateFeedbackForIdea?.(targetSuggestion.originalNode.id)
      }, 100)
    }

    // Remove from suggestions list
    setters.setSuggestions((prev) => prev.filter((s) => s.id !== suggestionId))

    // Update source node count
    if (parentNodeId) {
      setters.setSourceNodes((prev) => {
        const updated = new Map(prev)
        const existing = updated.get(parentNodeId)
        if (existing && existing.suggestionCount > 1) {
          updated.set(parentNodeId, { ...existing, suggestionCount: existing.suggestionCount - 1 })
        } else if (existing) {
          updated.delete(parentNodeId)
        }
        return updated
      })
    }

    // Mark as accepted in DB (fire and forget)
    if (targetSuggestion.dbId) {
      SuggestionsService.acceptSuggestion(targetSuggestion.dbId)
        .then((result) => {
          if (result.error) {
            console.error('Failed to accept suggestion in database:', result.error.message)
            Sentry.captureException(new Error(result.error.message), {
              tags: { feature: 'mindmap', action: 'accept_suggestion' },
            })
          }
        })
        .catch((err) => {
          console.error('Failed to accept suggestion in database:', err)
          Sentry.captureException(err, {
            tags: { feature: 'mindmap', action: 'accept_suggestion' },
          })
        })
    }
  } else {
    // Deselecting — remove node from mindmap
    callbacks.onRemoveNodes?.([targetSuggestion.originalNode.id])
    setters.setSuggestions((prev) =>
      prev.map((s) => (s.id === suggestionId ? { ...s, selected: false } : s))
    )
  }

  return Promise.resolve()
}

// ============================================================================
// SMALL STATE UPDATES
// ============================================================================

/**
 * Marks a suggestion as unselected when its node is deleted from the mindmap.
 */
export function markSuggestionNodeDeleted(
  deletedNodeId: string,
  setSuggestions: Dispatch<SetStateAction<AISuggestion[]>>
): void {
  setSuggestions((prev) =>
    prev.map((suggestion) => {
      if (suggestion.originalNode.id === deletedNodeId && suggestion.selected) {
        return { ...suggestion, selected: false }
      }
      return suggestion
    })
  )
}
